/**
 * notes-sync.js
 * Flushes local dirty notes to Supabase.
 *
 * Strategy: last-write-wins. `updated_locally_at` is the source of truth for which
 * version to push. No dependency graph needed (notes are standalone documents).
 *
 * Sync states per note:
 *   sync_status: 'synced'   — in sync with Supabase
 *   sync_status: 'pending'  — dirty, not yet attempted
 *   sync_status: 'failed'   — last sync attempt failed
 *
 * Dirty flags:
 *   dirty: true      — has unsent local changes
 *   _local_only: true — never reached Supabase (needs INSERT, not UPDATE)
 *   _deleted: true   — deleted locally, needs DELETE in Supabase
 */

import { notesStorage } from './notes-storage';
import * as notesApi from './notes-api';
import { logNote } from './activity-log';

let _syncTimer = null;
let _lastDistrictId = null;

/**
 * Schedule a debounced sync flush.
 * Multiple rapid calls (e.g. autosave typing) collapse into one network round-trip.
 * @param {string} districtId
 * @param {number} [delay=1500]
 */
export function queueSync(districtId, delay = 1500) {
  if (districtId) _lastDistrictId = districtId;
  if (_syncTimer) clearTimeout(_syncTimer);
  _syncTimer = setTimeout(() => {
    _syncTimer = null;
    syncNotes(districtId).catch(() => {});
  }, delay);
}

// Flush dirty notes when the browser regains connectivity.
// Covers the case where the user edits notes while offline and navigates away
// from the Notes page before reconnecting (queueSync's timer fired but syncNotes
// returned early because navigator.onLine was false at that moment).
if (typeof window !== 'undefined' && !window.__kevesNotesSyncListener) {
  window.__kevesNotesSyncListener = true;
  window.addEventListener('online', () => {
    if (_lastDistrictId) syncNotes(_lastDistrictId).catch(() => {});
  });
}

/**
 * Immediately flush all dirty notes for a district to Supabase.
 * Safe to call when offline — skips silently.
 * @param {string} districtId
 */
export async function syncNotes(districtId) {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;

  const dirty = await notesStorage.getDirty(districtId);
  if (dirty.length === 0) return;

  for (const note of dirty) {
    try {
      if (note._deleted) {
        // Locally deleted — remove from Supabase if it was ever synced
        if (!note._local_only) {
          await notesApi.deleteNote(note.id);
        }
        await notesStorage.delete(note.id);
      } else if (note._local_only) {
        // Never reached Supabase — INSERT
        await notesApi.createNote({
          id: note.id,
          district_id: note.district_id,
          title: note.title || '',
          content: note.content || '',
          tags: note.tags || [],
          note_type:       note.note_type        || 'text',
          checklist_items: note.checklist_items  || [],
          linked_type: note.linked_type || 'general',
          linked_id: note.linked_id || null,
          linked_label: note.linked_label || null,
          category:        note.category         || 'general',
          visibility:      note.visibility       || 'private',
          visibility_type: note.visibility_type  || 'district',
          is_reference:    note.is_reference     ?? false,
          pinned:          note.pinned           ?? false,
          archived:        note.archived         ?? false,
          created_at:      note.created_at,
          updated_at:      note.updated_locally_at || note.updated_at,
          updated_locally_at: note.updated_locally_at,
        });
        await notesStorage.markSynced(note.id);
        logNote(note, districtId); // best effort — no await, never throws
      } else {
        // Already exists in Supabase — UPDATE
        await notesApi.updateNote(note.id, {
          title: note.title || '',
          content: note.content || '',
          tags: note.tags || [],
          note_type:       note.note_type        || 'text',
          checklist_items: note.checklist_items  || [],
          linked_type: note.linked_type || 'general',
          linked_id: note.linked_id || null,
          linked_label: note.linked_label || null,
          category:        note.category         || 'general',
          visibility:      note.visibility       || 'private',
          visibility_type: note.visibility_type  || 'district',
          is_reference:    note.is_reference     ?? false,
          pinned:          note.pinned           ?? false,
          archived:        note.archived         ?? false,
          updated_locally_at: note.updated_locally_at,
        });
        await notesStorage.markSynced(note.id);
        logNote(note, districtId); // best effort — no await, never throws
      }
    } catch {
      await notesStorage.markFailed(note.id);
    }
  }
}
